/**
 * #2340 — Manager-review data for the Store Visit Summary (checkout) screen.
 *
 * Before a manager signs off, surface WHAT was captured this visit, grouped by
 * project so each task shows its own orders / inventory / out-of-stock. The data
 * is already captured locally by the createOrder write-op (STORES.VISIT_SINGLETONS
 * under `orders:<visitLocalId>`, each group tagged with its projectId); here we
 * join it to the cached product catalog for descriptions and aggregate per project.
 *
 * Read-only and offline-safe — it never blocks or alters the signature flow.
 */
import { STORES, get, getByIndex, type CachedProduct } from './indexedDB';

interface OrderLine {
  productid: number;
  orderqty: number;
  quantity_on_hand?: number;
  is_oos?: boolean;
  description?: string; // #2340 — captured at scan time; survives a cleared cache
}

interface OrdersSingleton {
  key: string;
  orders: Array<{
    localId: string;
    storeId: number;
    projectId?: number;
    products: OrderLine[];
    createdAt: string;
  }>;
}

export interface VisitOrderItem {
  productid: number;
  description: string;
  upc?: string;
  qty: number;
}

/** Per-project capture summary for the checkout review. */
export interface ProjectOrderReview {
  orders: VisitOrderItem[]; // lines with an order quantity
  oos: Array<{ productid: number; description: string; upc?: string }>; // out-of-stock
  inventory: Array<{ productid: number; description: string; upc?: string; onHand: number }>; // on-hand counts
  totalOrderUnits: number;
  hasAny: boolean;
}

/** Keyed by projectId. Project 0 holds any capture written before projectId existed. */
export type VisitOrderReview = Record<number, ProjectOrderReview>;

function describe(
  productMap: Map<number, CachedProduct>,
  productid: number,
  captured?: string,
): { description: string; upc?: string } {
  const p = productMap.get(productid);
  // Prefer the description captured on the order line (#2340), then the product
  // cache, then a #id fallback — so the name shows even if the cache is empty.
  const description = captured?.trim() || p?.description?.trim() || `Product #${productid}`;
  return { description, upc: p?.upc || undefined };
}

/**
 * Pure aggregation (unit-tested): collapse all captured order groups into one
 * per-product summary PER PROJECT. A product can appear once per project across
 * the three buckets: OOS wins (qty 0 shelf-tag scan), else an ordered line, plus
 * any on-hand count.
 */
export function summarizeByProject(
  groups: OrdersSingleton['orders'],
  productMap: Map<number, CachedProduct>,
): VisitOrderReview {
  // projectId -> accumulators
  const byProject = new Map<
    number,
    { orderQty: Map<number, number>; oosIds: Set<number>; onHand: Map<number, number>; descById: Map<number, string> }
  >();

  for (const group of groups) {
    const pid = group.projectId ?? 0;
    let acc = byProject.get(pid);
    if (!acc) {
      acc = { orderQty: new Map(), oosIds: new Set(), onHand: new Map(), descById: new Map() };
      byProject.set(pid, acc);
    }
    for (const line of group.products) {
      if (line.description?.trim()) acc.descById.set(line.productid, line.description.trim());
      if (line.is_oos) {
        acc.oosIds.add(line.productid);
      } else if (line.orderqty > 0) {
        acc.orderQty.set(line.productid, (acc.orderQty.get(line.productid) || 0) + line.orderqty);
      }
      if (line.quantity_on_hand !== undefined && !line.is_oos) {
        acc.onHand.set(line.productid, line.quantity_on_hand);
      }
    }
  }

  const review: VisitOrderReview = {};
  for (const [pid, acc] of byProject.entries()) {
    const orders: VisitOrderItem[] = [...acc.orderQty.entries()]
      .map(([productid, qty]) => ({ productid, qty, ...describe(productMap, productid, acc.descById.get(productid)) }))
      .sort((a, b) => a.description.localeCompare(b.description));
    const oos = [...acc.oosIds]
      .map((productid) => ({ productid, ...describe(productMap, productid, acc.descById.get(productid)) }))
      .sort((a, b) => a.description.localeCompare(b.description));
    const inventory = [...acc.onHand.entries()]
      .map(([productid, qty]) => ({ productid, onHand: qty, ...describe(productMap, productid, acc.descById.get(productid)) }))
      .sort((a, b) => a.description.localeCompare(b.description));
    review[pid] = {
      orders,
      oos,
      inventory,
      totalOrderUnits: orders.reduce((sum, o) => sum + o.qty, 0),
      hasAny: orders.length > 0 || oos.length > 0 || inventory.length > 0,
    };
  }
  return review;
}

/** Load + summarize this visit's captured orders/OOS/inventory, grouped by project. */
export async function getVisitOrderReview(
  visitLocalId: string | null | undefined,
  storeId: number,
): Promise<VisitOrderReview> {
  if (!visitLocalId) return {};
  try {
    const singleton = await get<OrdersSingleton>(STORES.VISIT_SINGLETONS, `orders:${visitLocalId}`);
    if (!singleton || !singleton.orders?.length) return {};
    const products = await getByIndex<CachedProduct>(STORES.PRODUCTS, 'storeId', storeId);
    const productMap = new Map<number, CachedProduct>(products.map((p) => [p.productid, p]));
    return summarizeByProject(singleton.orders, productMap);
  } catch (err) {
    console.error('[visitOrderSummary] failed to build', err);
    return {};
  }
}
